import React from "react";
import { config } from "@/config";
import {
  PublicAuthChallengeError,
  type PublicAuthChallengeAction,
  setPublicAuthChallengeTokenProvider,
  getPublicAuthChallengeFailureMessage,
} from "@/services/api/publicAuthChallenge";

type TurnstileWidgetId = string;
type TurnstileTheme = "light" | "dark" | "auto";
type TurnstileSize = "normal" | "compact" | "invisible";
type TurnstileExecution = "render" | "execute";
type CleanupCallback = () => void;

interface TurnstileRenderOptions {
  sitekey: string;
  action: string;
  theme: TurnstileTheme;
  size: TurnstileSize;
  execution: TurnstileExecution;
  callback: (token: string) => void;
  "error-callback": () => void;
  "expired-callback": () => void;
  "timeout-callback": () => void;
  "unsupported-callback": () => void;
}

interface TurnstileApi {
  render: (
    container: HTMLElement,
    options: TurnstileRenderOptions,
  ) => TurnstileWidgetId | undefined;
  execute: (widgetId: TurnstileWidgetId) => void;
  remove: (widgetId: TurnstileWidgetId) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export interface PublicAuthChallengeSettings {
  enabled: boolean;
  provider: "turnstile";
  turnstileSiteKey?: string;
  tokenTimeoutMs: number;
}

type ChallengeUiState = "idle" | "verifying" | "failed";

const TURNSTILE_SCRIPT_ID = "terramatrix-public-auth-turnstile";
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TURNSTILE_SCRIPT_STATUS = "terramatrixTurnstileStatus";

const getTurnstileApi = (): TurnstileApi | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window.turnstile;
};

const getExistingScript = (): HTMLScriptElement | null => {
  const script = document.getElementById(TURNSTILE_SCRIPT_ID);
  return script instanceof HTMLScriptElement ? script : null;
};

const setScriptStatus = (
  script: HTMLScriptElement,
  status: "loaded" | "error",
): void => {
  script.dataset[TURNSTILE_SCRIPT_STATUS] = status;
};

const getScriptStatus = (
  script: HTMLScriptElement,
): "loaded" | "error" | undefined => {
  const status = script.dataset[TURNSTILE_SCRIPT_STATUS];
  return status === "loaded" || status === "error" ? status : undefined;
};

const safelyRemoveWidget = (
  api: TurnstileApi,
  widgetId: TurnstileWidgetId,
): void => {
  try {
    api.remove(widgetId);
  } catch {
    // Widget cleanup is best-effort and must not prevent request settlement.
  }
};

const loadTurnstileApi = (
  addCleanup: (cleanup: CleanupCallback) => void,
): Promise<TurnstileApi> => {
  const existingApi = getTurnstileApi();
  if (existingApi) {
    return Promise.resolve(existingApi);
  }

  if (typeof document === "undefined") {
    return Promise.reject(new PublicAuthChallengeError());
  }

  return new Promise<TurnstileApi>((resolve, reject) => {
    let settled = false;

    const settleWithApi = () => {
      if (settled) {
        return;
      }

      const api = getTurnstileApi();
      if (api) {
        settled = true;
        resolve(api);
        return;
      }

      settled = true;
      reject(new PublicAuthChallengeError());
    };

    const settleFailure = () => {
      if (settled) {
        return;
      }

      settled = true;
      reject(new PublicAuthChallengeError());
    };

    const onLoad = () => {
      setScriptStatus(script, "loaded");
      settleWithApi();
    };

    const onError = () => {
      setScriptStatus(script, "error");
      settleFailure();
    };

    const existingScript = getExistingScript();
    const script = existingScript ?? document.createElement("script");
    const scriptStatus = getScriptStatus(script);

    if (scriptStatus === "loaded") {
      settleWithApi();
      return;
    }

    if (scriptStatus === "error") {
      settleFailure();
      return;
    }

    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
    addCleanup(() => {
      script.removeEventListener("load", onLoad);
      script.removeEventListener("error", onError);
    });

    if (!existingScript) {
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  });
};

interface ChallengeRequest {
  action: PublicAuthChallengeAction;
  promise: Promise<string | undefined>;
  cancel: () => void;
}

const runCleanupCallbacks = (callbacks: CleanupCallback[]): void => {
  while (callbacks.length > 0) {
    const cleanup = callbacks.pop();
    if (!cleanup) {
      continue;
    }

    try {
      cleanup();
    } catch {
      // Best-effort cleanup keeps request settlement deterministic.
    }
  }
};

const toTurnstileAction = (action: PublicAuthChallengeAction): string =>
  `auth-${action}`;

export const PublicAuthChallengeProvider: React.FC<{
  children: React.ReactNode;
  settings?: PublicAuthChallengeSettings;
}> = ({ children, settings = config.auth.publicChallenge }) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const activeWidgetIdRef = React.useRef<TurnstileWidgetId | null>(null);
  const inFlightRef = React.useRef<ChallengeRequest | null>(null);
  const isMountedRef = React.useRef(false);
  const [uiState, setUiState] = React.useState<ChallengeUiState>("idle");

  const removeActiveWidget = React.useCallback((api: TurnstileApi) => {
    const widgetId = activeWidgetIdRef.current;
    if (widgetId) {
      safelyRemoveWidget(api, widgetId);
      activeWidgetIdRef.current = null;
    }
  }, []);

  const safeSetUiState = React.useCallback((nextState: ChallengeUiState) => {
    if (isMountedRef.current) {
      setUiState(nextState);
    }
  }, []);

  const executeTurnstile = React.useCallback(
    async (action: PublicAuthChallengeAction): Promise<string | undefined> => {
      if (!settings.enabled) {
        return undefined;
      }

      if (!settings.turnstileSiteKey) {
        throw new PublicAuthChallengeError();
      }
      const siteKey = settings.turnstileSiteKey;

      const container = containerRef.current;
      if (!container) {
        throw new PublicAuthChallengeError();
      }

      const inFlight = inFlightRef.current;
      if (inFlight) {
        if (inFlight.action === action) {
          return inFlight.promise;
        }

        throw new PublicAuthChallengeError();
      }

      safeSetUiState("verifying");

      let cancelRequest: () => void = () => undefined;
      const challengePromise = new Promise<string>((resolve, reject) => {
        let settled = false;
        let renderedWidgetId: TurnstileWidgetId | null = null;
        let timeoutId: number | null = null;
        const cleanupCallbacks: CleanupCallback[] = [];

        const cleanup = () => {
          if (timeoutId !== null) {
            window.clearTimeout(timeoutId);
            timeoutId = null;
          }
          runCleanupCallbacks(cleanupCallbacks);
        };

        const settleFailure = () => {
          if (settled) {
            return;
          }

          settled = true;
          cleanup();
          reject(new PublicAuthChallengeError());
        };

        const settleSuccess = (token: string) => {
          if (settled) {
            return;
          }

          let trimmedToken = "";
          try {
            trimmedToken = token.trim();
          } catch {
            settleFailure();
            return;
          }

          if (!trimmedToken) {
            settleFailure();
            return;
          }

          settled = true;
          cleanup();
          resolve(trimmedToken);
        };

        timeoutId = window.setTimeout(
          settleFailure,
          settings.tokenTimeoutMs,
        );
        cancelRequest = settleFailure;

        loadTurnstileApi((callback) => cleanupCallbacks.push(callback))
          .then((api) => {
            if (settled) {
              return;
            }

            removeActiveWidget(api);

            let widgetId: TurnstileWidgetId | undefined;
            try {
              widgetId = api.render(container, {
                sitekey: siteKey,
                action: toTurnstileAction(action),
                theme: "auto",
                size: "invisible",
                execution: "execute",
                callback: settleSuccess,
                "error-callback": settleFailure,
                "expired-callback": settleFailure,
                "timeout-callback": settleFailure,
                "unsupported-callback": settleFailure,
              });
            } catch {
              settleFailure();
              return;
            }

            if (!widgetId) {
              settleFailure();
              return;
            }

            renderedWidgetId = widgetId;
            activeWidgetIdRef.current = widgetId;
            cleanupCallbacks.push(() => {
              if (renderedWidgetId) {
                safelyRemoveWidget(api, renderedWidgetId);
                if (activeWidgetIdRef.current === renderedWidgetId) {
                  activeWidgetIdRef.current = null;
                }
              }
            });

            try {
              api.execute(widgetId);
            } catch {
              settleFailure();
            }
          })
          .catch(() => settleFailure());
      })
        .then((token) => {
          safeSetUiState("idle");
          return token;
        })
        .catch((error: unknown) => {
          safeSetUiState("failed");
          throw error instanceof PublicAuthChallengeError
            ? error
            : new PublicAuthChallengeError();
        })
        .finally(() => {
          if (inFlightRef.current?.promise === challengePromise) {
            inFlightRef.current = null;
          }
        });

      inFlightRef.current = {
        action,
        promise: challengePromise,
        cancel: cancelRequest,
      };
      return challengePromise;
    },
    [
      removeActiveWidget,
      safeSetUiState,
      settings.enabled,
      settings.tokenTimeoutMs,
      settings.turnstileSiteKey,
    ],
  );

  React.useLayoutEffect(() => {
    isMountedRef.current = true;
    setPublicAuthChallengeTokenProvider(executeTurnstile);
    return () => {
      isMountedRef.current = false;
      setPublicAuthChallengeTokenProvider(null);
    };
  }, [executeTurnstile]);

  React.useEffect(() => {
    return () => {
      const inFlight = inFlightRef.current;
      if (inFlight) {
        inFlightRef.current = null;
        inFlight.cancel();
      }

      const api = getTurnstileApi();
      if (api) {
        removeActiveWidget(api);
      }
    };
  }, [removeActiveWidget]);

  return (
    <>
      {children}
      {settings.enabled && (
        <div
          ref={containerRef}
          aria-hidden="true"
          className="pointer-events-none fixed bottom-0 right-0 z-50 h-px w-px overflow-hidden opacity-0"
        />
      )}
      {uiState !== "idle" && (
        <div
          role={uiState === "failed" ? "alert" : "status"}
          aria-live="polite"
          className="fixed bottom-4 left-1/2 z-50 w-[calc(100vw-2rem)] max-w-sm -translate-x-1/2 rounded-md border border-border bg-background px-4 py-3 text-sm text-foreground shadow-lg"
        >
          {uiState === "verifying"
            ? "Verifying request..."
            : getPublicAuthChallengeFailureMessage()}
        </div>
      )}
    </>
  );
};
